import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CalendarClock, PhoneOutgoing, Plus, X, Save, Trash2, Loader2, AlertTriangle } from 'lucide-react';
import { campaignsAPI } from '../services/api.js';
import { formatDateTimeWithZone, formatPhone, toE164 } from '../lib/format.js';
import { isoToZonedInput, zonedInputToIso, zoneAbbreviation } from '../lib/tz.js';

// FOLLOW-UP CALLBACKS for ONE business.
//
// "Call this company back on Thursday about the driver pile." Unlimited per
// business, each with its own REQUIRED matter — a callback with no reason is just
// a redial, and the matter is what the AI is told the call is about.
//
// Times are stored UTC and entered in the TENANT's zone (not the browser's), the
// same convention the rest of the campaign scheduling uses. One tenant, one clock,
// whoever is looking.
//
// Two server behaviours the UI has to respect rather than re-implement:
//   • A FIRED callback is history. The route 409s on edit/cancel, because changing
//     what we said we were calling about would rewrite the record. So the form is
//     only offered on `pending` rows.
//   • Call now is ALLOWED while manually paused (a human deliberately placing one
//     call) and REFUSED when the health monitor auto-paused the campaign. That
//     refusal carries `pauseReason`, so this explains it instead of just failing.

const STATUS = {
  pending: { label: 'Scheduled', cls: 'bg-sky-950/60 text-sky-300' },
  fired: { label: 'Called', cls: 'bg-emerald-950/60 text-emerald-300' },
  done: { label: 'Completed', cls: 'bg-stone-800 text-stone-300' },
  canceled: { label: 'Canceled', cls: 'bg-stone-800 text-stone-500' }
};

export default function ContactCallbacks({ campaignId, contact, canEdit = false }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ when: '', matter: '' });
  const [editing, setEditing] = useState({}); // id -> { when, matter }
  const [busy, setBusy] = useState(null);
  const [calling, setCalling] = useState(false);
  const [callNowMatter, setCallNowMatter] = useState('');
  // ✅ UNBLOCKED 2026-08-15. This was true since v1.65.0 because the Call-now route
  // called `campaignService.isAutoPaused`, a named export missing from the default
  // export object — undefined(), an uncaught throw in an async handler, and on Node ≥15
  // that TERMINATES the process. Three presses killed core three times on 08-05.
  //
  // The fix is now in the deployed build: `isAutoPaused` is listed in the default export
  // (campaignService.js:1225) and the running core answers on the routes that shipped
  // with it. Verified against the REAL host — api.voice.relaywork.co, not the
  // railway.app subdomain I had been probing, which 404s everything and had me
  // reporting "not deployed" for days.
  const CALL_NOW_BLOCKED = false;
  const [showCallNow, setShowCallNow] = useState(false);
  // ── REHEARSE A FOLLOW-UP ON MY OWN PHONE ───────────────────────────────────
  // Brian: "I'm putting notes in there for the callback, and I need to trigger a test
  // call to myself to make sure it's going to come across the way it is intended."
  //
  // Dials YOU through the campaign's exact config with this callback's matter as the
  // objective, using the same builder the real dialer uses — so the rehearsal cannot
  // drift from the real thing.
  //
  // ⚠️ NOT the Call-now path. That one runs `campaignService.isAutoPaused` and is what
  // took core down five times on 08-05 (still blocked above). This is
  // POST /campaigns/:id/test-call, a different handler that never touches it.
  const [testTo, setTestTo] = useState(() => {
    try { return localStorage.getItem('alfred.testCallTo') || ''; } catch { return ''; }
  });
  const [testingId, setTestingId] = useState(null);
  const [testResult, setTestResult] = useState(null); // { ok, text }

  const rehearse = async (cb) => {
    // Normalise rather than refuse. This used to demand strict E.164 and rejected
    // "+17372728567." over a dictated full stop — the button did nothing and the
    // explanation blamed the format.
    const to = toE164(testTo);
    if (!to) {
      setTestResult({ ok: false, text: `“${testTo.trim()}” isn’t a number I can dial. A US number needs 10 digits, or start with + and the country code.` });
      return;
    }
    try { localStorage.setItem('alfred.testCallTo', to); } catch { /* private mode */ }
    setTestTo(to); // show back exactly what will be dialled
    setTestingId(cb.id);
    setTestResult(null);
    try {
      const r = await campaignsAPI.testCall(campaignId, { toE164: to, callbackMatter: cb.matter });
      // ⛔ THE ECHO IS THE WHOLE GUARD. `testCallSchema` is not strict, so a core that
      // predates the patch strips `callbackMatter` and places a perfectly successful
      // call — running the COLD PITCH. Without this check Brian would hear a cold open,
      // decide his follow-up notes read badly, and rewrite notes that were never used.
      if (r?.data?.usedCallbackMatter) {
        setTestResult({ ok: true, text: `Calling ${to} now — you should hear the follow-up opener, and the objective should be your note.` });
      } else {
        setTestResult({
          ok: false,
          text: `⚠️ The call is going out, but core is running a build that ignores the follow-up note — so what you are about to hear is the campaign's COLD PITCH, not this callback. Hang up; it proves nothing. Core needs docs/patches/core-callback-test-call-2026-08-14.md.`
        });
      }
    } catch (err) {
      setTestResult({ ok: false, text: err?.body?.message || err.message || 'Could not place the test call.' });
    } finally {
      setTestingId(null);
    }
  };

  // NB: zoneAbbreviation(date, tz) — the first argument is a DATE. The rest of
  // the campaign page omits tz entirely and uses the module zone that
  // AuthContext primes, so match that rather than threading it through.
  const zone = zoneAbbreviation();

  const load = async () => {
    setLoading(true);
    try {
      const r = await campaignsAPI.listCallbacks(campaignId, contact.id);
      setItems(r?.data || []);
    } catch (err) {
      if (err.status !== 404) setError(err.message || 'Could not load follow-ups.');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [campaignId, contact.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const fail = (err, fallback) => {
    // The server distinguishes its refusals so we can explain them. Do that.
    const body = err.body || {};
    if (body.error === 'campaign_auto_paused') {
      setError(
        `${body.message} ${body.pauseReason ? `What tripped it: ${body.pauseReason}` : ''}`.trim()
      );
      return;
    }
    if (body.error === 'contact_opted_out' || body.error === 'on_dnc') {
      setError('This business has opted out — it cannot be called or scheduled.');
      return;
    }
    if (body.error === 'not_pending') {
      setError('That follow-up has already been called, so it can no longer be changed. Schedule another instead.');
      load();
      return;
    }
    setError(body.message || err.message || fallback);
  };

  const create = async () => {
    const matter = draft.matter.trim();
    if (!matter) { setError('Say what the call is about — that is what the AI is told.'); return; }
    if (!draft.when) { setError('Pick a date and time.'); return; }
    setBusy('new');
    setError('');
    try {
      const r = await campaignsAPI.createCallback(campaignId, contact.id, {
        scheduledAt: zonedInputToIso(draft.when),
        matter
      });
      setItems((l) => [...l, r?.data].filter(Boolean));
      setDraft({ when: '', matter: '' });
      setAdding(false);
    } catch (err) { fail(err, 'Could not schedule the follow-up.'); }
    finally { setBusy(null); }
  };

  const saveEdit = async (cb) => {
    const d = editing[cb.id];
    if (!d) return;
    const matter = (d.matter || '').trim();
    if (!matter) { setError('A follow-up needs a reason.'); return; }
    setBusy(cb.id);
    setError('');
    try {
      const r = await campaignsAPI.updateCallback(campaignId, cb.id, {
        scheduledAt: zonedInputToIso(d.when),
        matter
      });
      const updated = r?.data || r;
      setItems((l) => l.map((x) => (x.id === cb.id ? updated : x)));
      setEditing((e) => { const n = { ...e }; delete n[cb.id]; return n; });
    } catch (err) { fail(err, 'Could not update the follow-up.'); }
    finally { setBusy(null); }
  };

  const cancel = async (cb) => {
    if (!window.confirm(`Cancel the follow-up on ${formatDateTimeWithZone(cb.scheduledAt)}?`)) return;
    setBusy(cb.id);
    try {
      const r = await campaignsAPI.cancelCallback(campaignId, cb.id);
      const updated = r?.data || r;
      setItems((l) => l.map((x) => (x.id === cb.id ? updated : x)));
    } catch (err) { fail(err, 'Could not cancel.'); }
    finally { setBusy(null); }
  };

  // ⛔ CALL NOW IS DISABLED — THIS BUTTON CRASHES PRODUCTION CORE.
  //
  // POST /campaigns/:id/contacts/:cid/call runs
  //   if (campaignService.isAutoPaused(campaign))        (campaigns.js:962)
  // and the RUNNING build has that caller without the definition — a half-shipped
  // working-tree upload. `isAutoPaused` is undefined, the route throws, and in
  // Node 20 an unhandled rejection terminates the process. So every press takes the
  // whole API down: the dashboard, the dialer, everything.
  //
  // Measured twice, both from Brian pressing it:
  //   08:05:42 / 08:05:50 / 08:06:13 UTC  TypeError: isAutoPaused is not a function
  //   19:01 UTC  core uptime 63s — restarted the moment he pressed it again
  //
  // The fix is a CORE deploy, not a dashboard change: `campaignService.js` (which
  // defines isAutoPaused, line 300) has to ship alongside `campaigns.js`. Both are
  // consistent on origin/feat/box-first-bridge already — prod is just running an
  // older mismatched build.
  //
  // Until then this refuses locally rather than firing a request that kills prod.
  // ⚠️ REMOVE THIS GUARD once core reports a build with isAutoPaused defined —
  // it is a stopgap, not the design.
  const callNow = async () => {
    if (CALL_NOW_BLOCKED) {
      setError('Call now is temporarily disabled — the server route for it crashes the API '
        + '(campaignService.isAutoPaused is missing from the running build). It needs a core '
        + 'deploy, not a dashboard fix. Schedule a follow-up instead, or use Call now on the '
        + 'Outbound Calls page, which uses a different, working route.');
      setShowCallNow(false);
      return;
    }
    setCalling(true);
    setError('');
    setNotice('');
    try {
      const r = await campaignsAPI.callContactNow(campaignId, contact.id, {
        matter: callNowMatter.trim() || undefined
      });
      const callId = r?.data?.callId;
      setNotice(callId ? 'Calling now — the transcript appears below once the call ends.' : 'Calling now.');
      setShowCallNow(false);
      setCallNowMatter('');
      // Core writes an already-fired callback row for a manual call, so the
      // history below stays complete rather than showing a call from nowhere.
      load();
    } catch (err) { fail(err, 'Could not place the call.'); }
    finally { setCalling(false); }
  };

  const pending = items.filter((c) => c.status === 'pending');
  const past = items.filter((c) => c.status !== 'pending');

  return (
    <div className="rounded-lg border border-stone-800 bg-stone-900/30 p-3">
      <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <CalendarClock size={14} className="text-stone-400" />
          <h4 className="text-sm font-medium text-stone-200">Follow-up calls</h4>
          {pending.length > 0 && (
            <span className="badge bg-sky-950/60 text-sky-300 text-[10px]">{pending.length} scheduled</span>
          )}
        </div>
        {canEdit && (
          <div className="flex items-center gap-1">
            {!adding && (
              <button type="button" className="btn-ghost" onClick={() => { setAdding(true); setError(''); }}>
                <Plus size={13} /> Schedule
              </button>
            )}
            {/* Disabled at the surface too, not just in the handler — a live-looking
                button that refuses on click is still an invitation to press it, and
                pressing it is what took prod down twice. */}
            {!showCallNow && (
              <button
                type="button"
                className="btn-ghost disabled:opacity-40 disabled:cursor-not-allowed"
                disabled={CALL_NOW_BLOCKED}
                title={CALL_NOW_BLOCKED
                  ? 'Temporarily disabled — this route crashes the API and needs a core deploy. Schedule a follow-up instead.'
                  : 'Place this call now'}
                onClick={() => { setShowCallNow(true); setError(''); }}
              >
                <PhoneOutgoing size={13} /> Call now
              </button>
            )}
          </div>
        )}
      </div>
      <p className="mb-3 text-xs text-stone-500">
        Call this business back at a set time about a specific matter. What you write becomes the
        AI&rsquo;s goal for that call{zone ? ` · times are ${zone}` : ''}.
      </p>

      {error && (
        <p className="mb-3 flex items-start gap-2 rounded border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-200">
          <AlertTriangle size={13} className="mt-0.5 shrink-0" /> <span>{error}</span>
        </p>
      )}
      {notice && (
        <p className="mb-3 rounded border border-emerald-800 bg-emerald-950/40 px-3 py-2 text-xs text-emerald-200">{notice}</p>
      )}

      {/* Where a rehearsal call goes. Remembered locally so it is typed once, and never
          defaulted to a guess — a wrong number here dials a stranger. */}
      {canEdit && items.length > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-2 text-xs">
          <label className="text-stone-500" htmlFor="rehearse-to">Test calls go to</label>
          <input
            id="rehearse-to"
            className="field-input py-1 px-2 text-xs w-44"
            placeholder="+17372728567"
            value={testTo}
            onChange={(e) => setTestTo(e.target.value)}
            aria-label="Number to place test calls to, E.164"
          />
          <span className="text-stone-600">
            {toE164(testTo)
              ? <>dials <span className="text-stone-400">{formatPhone(toE164(testTo))}</span> — your own phone, never the business</>
              : 'your own phone — a rehearsal never dials the business'}
          </span>
        </div>
      )}
      {testResult && (
        <p className={`mb-3 flex items-start gap-2 rounded border px-3 py-2 text-xs ${testResult.ok
          ? 'border-emerald-800 bg-emerald-950/40 text-emerald-200'
          : 'border-amber-600/50 bg-amber-950/30 text-amber-200'}`}>
          {!testResult.ok && <AlertTriangle size={13} className="mt-0.5 shrink-0" />}
          <span>{testResult.text}</span>
        </p>
      )}

      {showCallNow && (
        <div className="mb-3 space-y-2 rounded-lg border border-alfred-gold/30 p-3">
          <label className="field-label text-xs">Objective of this call (optional)</label>
          <textarea
            className="field-input min-h-[56px] text-sm"
            placeholder="e.g. following up on the driver applications they mentioned last week"
            value={callNowMatter}
            onChange={(e) => setCallNowMatter(e.target.value)}
            maxLength={2000}
          />
          <div className="flex gap-2">
            <button type="button" className="btn-primary" onClick={callNow} disabled={calling}>
              {calling ? <Loader2 size={13} className="animate-spin" /> : <PhoneOutgoing size={13} />}
              {calling ? 'Calling…' : `Call ${contact.company || contact.name || 'this business'} now`}
            </button>
            <button type="button" className="btn-ghost" onClick={() => setShowCallNow(false)} disabled={calling}>
              <X size={13} /> Cancel
            </button>
          </div>
        </div>
      )}

      {adding && (
        <div className="mb-3 space-y-2 rounded-lg border border-alfred-gold/30 p-3">
          <div>
            <label className="field-label text-xs">When {zone ? `(${zone})` : ''}</label>
            <input
              type="datetime-local"
              className="field-input text-sm"
              value={draft.when}
              onChange={(e) => setDraft((d) => ({ ...d, when: e.target.value }))}
            />
          </div>
          <div>
            <label className="field-label text-xs">Objective of this call</label>
            <textarea
              className="field-input min-h-[56px] text-sm"
              placeholder="e.g. they said to call back after the 15th about the two open lanes"
              value={draft.matter}
              onChange={(e) => setDraft((d) => ({ ...d, matter: e.target.value }))}
              maxLength={2000}
            />
            <p className="mt-1 text-xs text-stone-500">
              Required. The AI is told this is a follow-up and that this is the objective.
              {' '}⚠️ It currently rides <em>alongside</em> the campaign pitch rather than replacing
              it — Brian has asked for objective-only calls and that change is with Charlie.
            </p>
          </div>
          <div className="flex gap-2">
            <button type="button" className="btn-primary" onClick={create} disabled={busy === 'new'}>
              <Save size={13} /> Schedule
            </button>
            <button type="button" className="btn-ghost" onClick={() => { setAdding(false); setError(''); }}>
              <X size={13} /> Cancel
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <p className="text-xs text-stone-500">Loading…</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-stone-500">No follow-ups scheduled for this business.</p>
      ) : (
        <ul className="divide-y divide-stone-900">
          {[...pending, ...past].map((cb) => {
            const d = editing[cb.id];
            const s = STATUS[cb.status] || { label: cb.status, cls: 'bg-stone-800 text-stone-300' };
            if (d) {
              return (
                <li key={cb.id} className="space-y-2 py-3">
                  <input
                    type="datetime-local"
                    className="field-input text-sm"
                    value={d.when}
                    onChange={(e) => setEditing((s2) => ({ ...s2, [cb.id]: { ...s2[cb.id], when: e.target.value } }))}
                  />
                  <textarea
                    className="field-input min-h-[56px] text-sm"
                    value={d.matter}
                    onChange={(e) => setEditing((s2) => ({ ...s2, [cb.id]: { ...s2[cb.id], matter: e.target.value } }))}
                    maxLength={2000}
                  />
                  <div className="flex gap-2">
                    <button type="button" className="btn-primary" onClick={() => saveEdit(cb)} disabled={busy === cb.id}>
                      <Save size={13} /> Save
                    </button>
                    <button
                      type="button"
                      className="btn-ghost"
                      onClick={() => setEditing((e) => { const n = { ...e }; delete n[cb.id]; return n; })}
                    >
                      <X size={13} /> Cancel
                    </button>
                  </div>
                </li>
              );
            }
            return (
              <li key={cb.id} className="py-3">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm text-stone-200">
                        {formatDateTimeWithZone(cb.scheduledAt)}
                      </span>
                      <span className={`badge ${s.cls} text-[10px]`}>{s.label}</span>
                    </div>
                    <p className="mt-1 whitespace-pre-wrap text-sm text-stone-300">{cb.matter}</p>
                    {cb.callId && (
                      <Link to={`/calls/${cb.callId}`} className="mt-1 inline-block text-xs text-alfred-gold hover:underline">
                        Open the call →
                      </Link>
                    )}
                  </div>
                  {/* Only pending rows are editable — a fired callback is history,
                      and the server 409s rather than let it be rewritten. */}
                  {canEdit && (
                    <div className="flex shrink-0 items-center gap-1">
                      {/* Offered on FIRED rows too — hearing how a follow-up that already
                          went out actually sounded is the same question, after the fact. */}
                      <button
                        type="button"
                        className="btn-ghost"
                        onClick={() => rehearse(cb)}
                        disabled={testingId === cb.id}
                        title="Dial my number with this follow-up's notes as the objective"
                      >
                        {testingId === cb.id
                          ? <Loader2 size={13} className="animate-spin" />
                          : <PhoneOutgoing size={13} />}
                        Test this on me
                      </button>
                    </div>
                  )}
                  {canEdit && cb.status === 'pending' && (
                    <div className="flex shrink-0 items-center gap-1">
                      <button
                        type="button"
                        className="btn-ghost"
                        onClick={() => setEditing((e) => ({
                          ...e,
                          [cb.id]: { when: isoToZonedInput(cb.scheduledAt), matter: cb.matter }
                        }))}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="btn-ghost text-red-300 hover:text-red-200"
                        onClick={() => cancel(cb)}
                        disabled={busy === cb.id}
                        aria-label="Cancel follow-up"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
